import type { MapElement, Point, Utility } from "@cs2/contracts";
import { utilityEffectAreas, utilityTrajectoryPoints } from "@cs2/physics";

/**
 * Wie stark ein schrittgebundenes Objekt (Route-Element oder Utility)
 * gegenüber dem gewählten Schritt abgedunkelt wird. `dimmedOpacity` bleibt
 * Sache des Aufrufers — Quickview und Editor dimmen unterschiedlich stark
 * (0.3 bzw. 0.35), das ist bestehendes Verhalten, keine Divergenz zum Fixen.
 */
export function stepOpacity(
  stepOrder: number | null,
  selectedStepOrder: number | null,
  dimmedOpacity: number,
): number {
  if (selectedStepOrder === null || stepOrder === null) return 1;
  return stepOrder === selectedStepOrder ? 1 : dimmedOpacity;
}

/**
 * `MapElement.geometry` ist absichtlich `z.record(string, unknown())` (siehe
 * `packages/contracts/src/domain/tactic.ts`) — ein Cast ist an dieser Stelle
 * unvermeidbar. Ein Zugriff pro Form statt einem Cast je Renderer.
 */
export function getPointGeometry(element: MapElement): Point {
  return element.geometry as Point;
}

export function getArrowGeometry(element: MapElement): {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
} {
  return element.geometry as { x1: number; y1: number; x2: number; y2: number };
}

export function getHighlightGeometry(element: MapElement): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return element.geometry as {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

/**
 * Flugbahn, Landepunkt, Bogen-Ziehpunkt und Wirkflächen einer geworfenen
 * Utility — von Quickview (Wurf-/Blüh-Animation) und Editor (Arc-Drag-Handle)
 * bisher unabhängig berechnet, im Editor sogar unmemoized bei jedem Render.
 */
export function thrownUtilityGeometry(utility: Utility): {
  trajectory: Point[];
  landing: Point;
  arcHandle: Point;
  effectAreas: ReturnType<typeof utilityEffectAreas>;
} {
  const trajectory = utilityTrajectoryPoints(utility);
  const landing = trajectory.at(-1) ?? utility.target;
  const arcHandle = trajectory[1] ?? landing;
  const effectAreas = utilityEffectAreas(utility);
  return { trajectory, landing, arcHandle, effectAreas };
}
